// Global Product Search Functionality
// This script enables product search across all pages

import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../data/firebase/FirebaseConfig';

let allProducts: any[] = [];
let productsLoaded = false;
let searchTimeout: NodeJS.Timeout;

// Normalize text by removing special characters and accents for fuzzy matching
function normalizeSearchText(text: string): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[\/\-_.,;:!?'"()[\]{}|\\@#$%^&*+=<>~`]/g, '') // Remove special chars
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
}

// Load all products from Firebase
async function loadAllProducts() {
  if (productsLoaded && allProducts.length > 0) {
    return allProducts;
  }
  
  try {
    const productsSnapshot = await getDocs(collection(db, 'products'));
    
    allProducts = productsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    productsLoaded = true;
    return allProducts;
  } catch (error) {
    return [];
  }
}

// Search products by query
function searchProducts(query: string, products: any[]) {
  if (!query || query.trim().length === 0) {
    return [];
  }
  
  // Normalize search term for fuzzy matching
  const searchTerm = normalizeSearchText(query);
  const searchTermOriginal = query.toLowerCase().trim();
  const results = [];
  
  for (const product of products) {
    // Get original values (for exact matching)
    const namePtOriginal = (product.productNamePt || '').toLowerCase();
    const nameEnOriginal = (product.productNameEn || '').toLowerCase();
    const brandOriginal = (product.productBrand || '').toLowerCase();
    const codeOriginal = (product.productCode || '').toLowerCase();
    const descPtOriginal = (product.productDescriptionPt || '').toLowerCase();
    const descEnOriginal = (product.productDescriptionEn || '').toLowerCase();
    
    // Get normalized values (for fuzzy matching without special chars)
    const namePt = normalizeSearchText(product.productNamePt);
    const nameEn = normalizeSearchText(product.productNameEn);
    const brand = normalizeSearchText(product.productBrand);
    const code = normalizeSearchText(product.productCode);
    const descPt = normalizeSearchText(product.productDescriptionPt);
    const descEn = normalizeSearchText(product.productDescriptionEn);
    
    // Check if search term matches any field (both original and normalized)
    const matchesOriginal = namePtOriginal.includes(searchTermOriginal) || 
        nameEnOriginal.includes(searchTermOriginal) || 
        brandOriginal.includes(searchTermOriginal) || 
        codeOriginal.includes(searchTermOriginal) ||
        descPtOriginal.includes(searchTermOriginal) ||
        descEnOriginal.includes(searchTermOriginal);
    
    const matchesNormalized = namePt.includes(searchTerm) || 
        nameEn.includes(searchTerm) || 
        brand.includes(searchTerm) || 
        code.includes(searchTerm) ||
        descPt.includes(searchTerm) ||
        descEn.includes(searchTerm);
    
    if (matchesOriginal || matchesNormalized) {
      results.push(product);
    }
  }
  
  // Sort by relevance (exact matches first, then partial matches)
  const normalizedTerm = searchTerm;
  results.sort((a, b) => {
    const aNamePt = normalizeSearchText(a.productNamePt);
    const aNameEn = normalizeSearchText(a.productNameEn);
    const bNamePt = normalizeSearchText(b.productNamePt);
    const bNameEn = normalizeSearchText(b.productNameEn);
    
    const aStarts = aNamePt.startsWith(normalizedTerm) || aNameEn.startsWith(normalizedTerm);
    const bStarts = bNamePt.startsWith(normalizedTerm) || bNameEn.startsWith(normalizedTerm);
    
    if (aStarts && !bStarts) return -1;
    if (!aStarts && bStarts) return 1;
    return 0;
  });
  
  return results.slice(0, 10); // Limit to 10 results
}

// Display search results
function displaySearchResults(results: any[], container: HTMLElement | null, searchQuery: string) {
  if (!container) return;
  
  container.innerHTML = '';
  
  if (results.length === 0) {
    if (searchQuery.trim().length > 0) {
      // Get current language
      const currentLang = (window as any).LanguageContext?.getCurrentLanguage?.() || 
                         localStorage.getItem('languagePreference') || 
                         'pt';
      const noResultsText = currentLang === 'pt' ? 'Nenhum produto encontrado' : 'No products found';
      container.innerHTML = `<div class="search-result-item search-no-results"><p>${noResultsText}</p></div>`;
    }
    return;
  }
  
  results.forEach(product => {
    const resultItem = document.createElement('div');
    resultItem.className = 'search-result-item';
    resultItem.setAttribute('data-product-id', product.id);
    
    // Get product image or use placeholder
    const imageUrl = (product.productImages && product.productImages.length > 0) 
      ? product.productImages[0] 
      : '';
    
    // Get current language
    const currentLang = (window as any).LanguageContext?.getCurrentLanguage?.() || 
                       localStorage.getItem('languagePreference') || 
                       'pt';
    
    // Get product name based on language
    const productName = currentLang === 'pt' 
      ? (product.productNamePt || product.productNameEn || 'Product')
      : (product.productNameEn || product.productNamePt || 'Product');
    
    const productBrand = product.productBrand || '';
    const productPrice = product.productPrice ? `€${product.productPrice}` : '';
    
    resultItem.innerHTML = `
      <a href="/produto/${product.id}" class="search-result-link">
        ${imageUrl ? `<img src="${imageUrl}" alt="${productName}" class="search-result-image" onerror="this.style.display='none'">` : '<div class="search-result-image-placeholder"><i class="fa fa-box"></i></div>'}
        <div class="search-result-info">
          <h5 class="search-result-name">${productName}</h5>
          ${productBrand ? `<p class="search-result-brand">${productBrand}</p>` : ''}
          ${productPrice ? `<span class="search-result-price">${productPrice}</span>` : ''}
        </div>
      </a>
    `;
    
    container.appendChild(resultItem);
  });
}

// Initialize global product search
export function initializeGlobalProductSearch() {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupSearch);
  } else {
    setupSearch();
  }
}

// Setup search functionality
async function setupSearch() {
  // Find search popup elements
  const searchTogglers = document.querySelectorAll('.searcher-toggler-box');
  const searchPopups = document.querySelectorAll('.search-popup');
  const searchInputs = document.querySelectorAll('.search-popup input[name="search-field"]') as NodeListOf<HTMLInputElement>;
  
  if (searchPopups.length === 0 || searchInputs.length === 0) {
    // Retry after a delay if elements aren't loaded yet
    setTimeout(setupSearch, 1000);
    return;
  }
  
  // Load products when search popup is opened
  searchTogglers.forEach(toggler => {
    toggler.addEventListener('click', async function(e) {
      e.preventDefault();
      // Ensure search popup opens
      document.body.classList.add('search-active');
      
      // Load products if not already loaded
      if (!productsLoaded) {
        await loadAllProducts();
      }
      
      // Focus on search input
      setTimeout(() => {
        const input = document.querySelector('.search-popup input[name="search-field"]') as HTMLInputElement;
        if (input) {
          input.focus();
        }
      }, 300);
    });
  });
  
  // Setup close button handlers (using event delegation for reliability)
  document.addEventListener('click', function(e) {
    // Check if clicked element is close button or inside close button
    const target = e.target as HTMLElement;
    const closeBtn = target.closest('.close-search');
    if (closeBtn) {
      e.preventDefault();
      e.stopPropagation();
      // Remove search-active class
      document.body.classList.remove('search-active');
      // Clear search input and results
      const inputs = document.querySelectorAll('.search-popup input[name="search-field"]') as NodeListOf<HTMLInputElement>;
      inputs.forEach(input => {
        input.value = '';
      });
      // Clear all results containers
      const allResultsContainers = document.querySelectorAll('.search-results-container');
      allResultsContainers.forEach(container => {
        container.innerHTML = '';
      });
      return false;
    }
  });
  
  // Setup color layer (background) close handlers
  const colorLayers = document.querySelectorAll('.search-popup .color-layer');
  colorLayers.forEach(layer => {
    layer.addEventListener('click', function() {
      // Remove search-active class
      document.body.classList.remove('search-active');
      // Clear search input and results
      searchInputs.forEach(input => {
        input.value = '';
      });
      // Clear all results containers
      const allResultsContainers = document.querySelectorAll('.search-results-container');
      allResultsContainers.forEach(container => {
        container.innerHTML = '';
      });
    });
  });
  
  // Create results container for each search popup
  searchPopups.forEach(popup => {
    // Check if results container already exists
    let resultsContainer = popup.querySelector('.search-results-container');
    
    if (!resultsContainer) {
      resultsContainer = document.createElement('div');
      resultsContainer.className = 'search-results-container';
      const form = popup.querySelector('form');
      if (form) {
        form.appendChild(resultsContainer);
      } else {
        popup.appendChild(resultsContainer);
      }
    }
  });
  
  // Setup search input handlers
  searchInputs.forEach(input => {
    // Prevent form submission
    const form = input.closest('form');
    if (form) {
      form.addEventListener('submit', function(e) {
        e.preventDefault();
        handleSearch(input);
      });
    }
    
    // Real-time search as user types
    input.addEventListener('input', function() {
      clearTimeout(searchTimeout);
      const query = input.value;
      
      // Clear results if query is empty
      if (!query || query.trim().length === 0) {
        const popup = input.closest('.search-popup');
        const resultsContainer = popup?.querySelector('.search-results-container');
        if (resultsContainer) {
          resultsContainer.innerHTML = '';
        }
        return;
      }
      
      // Debounce search
      searchTimeout = setTimeout(() => {
        handleSearch(input);
      }, 300);
    });
    
    // Handle Enter key
    input.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSearch(input);
      }
    });
  });
}

// Handle search
async function handleSearch(input: HTMLInputElement) {
  const query = input.value.trim();
  const popup = input.closest('.search-popup');
  const resultsContainer = popup?.querySelector('.search-results-container') as HTMLElement;
  
  if (!resultsContainer) return;
  
  if (!query || query.length === 0) {
    resultsContainer.innerHTML = '';
    return;
  }
  
  // Get current language
  const currentLang = (window as any).LanguageContext?.getCurrentLanguage?.() || 
                     localStorage.getItem('languagePreference') || 
                     'pt';
  const searchingText = currentLang === 'pt' ? 'A pesquisar...' : 'Searching...';
  
  // Show loading state
  resultsContainer.innerHTML = `<div class="search-result-item search-loading"><p>${searchingText}</p></div>`;
  
  // Ensure products are loaded
  if (!productsLoaded) {
    await loadAllProducts();
  }
  
  // Search products
  const results = searchProducts(query, allProducts);
  
  // Display results
  displaySearchResults(results, resultsContainer, query);
}

// Make functions available globally
(window as any).GlobalProductSearch = {
  loadAllProducts,
  searchProducts,
  displaySearchResults,
  initializeGlobalProductSearch
};

